/**
 * Money / VAT — integer pence only inside the invoicing engine.
 *
 * Rounding method (matches accounting systems including Xero):
 * - All money values are integer pence. Convert at I/O boundaries only
 *   (DB numeric / display strings ↔ pence).
 * - Per-line net is already whole pence (from rates stored to 2dp).
 * - Per-line VAT = round_half_away(net_pence * vat_bps / 10_000),
 *   computed with integer arithmetic (no IEEE floats).
 * - VAT Total = sum of per-line VAT pence.
 * - Net Total = sum of per-line net pence.
 * - GBP Total = Net Total + VAT Total.
 *
 * Citation — Xero rounding:
 * Official how-to: https://developer.xero.com/documentation/guides/how-to-guides/rounding-in-xero
 * (referenced by Xero staff: line amounts are handled at 2dp and tax is calculated
 * on each line). Integrator docs (e.g. CloudDepot KB) state the same: tax per line,
 * round to 2 decimal places, then sum. That is the method implemented here so a
 * preview can be compared to a Xero draft without spurious penny differences from
 * “VAT on the subtotal” vs “VAT per line”.
 *
 * VAT rate comes from tenant_settings.standard_vat_rate_bps (data), not a code
 * constant. tax_treatment selects whether that rate applies (standard) or 0
 * (zero_rated / reverse_charge).
 */

export type TaxTreatment = 'standard' | 'zero_rated' | 'reverse_charge';

/** Convert DB/API pounds (string or number at ≤2dp) to integer pence. */
export function poundsToPence(v: number | string | null | undefined): number | null {
  if (v === null || v === undefined || v === '') return null;
  const raw = typeof v === 'number' ? decimalStringFromNumber(v) : String(v).trim();
  if (!/^-?\d+(\.\d{1,2})?$/.test(raw) && !/^-?\d+\.\d{3,}$/.test(raw)) {
    const n = Number(raw);
    if (!Number.isFinite(n)) return null;
    return Math.round(n * 100);
  }
  const neg = raw.startsWith('-');
  const abs = neg ? raw.slice(1) : raw;
  const [whole, frac = ''] = abs.split('.');
  const frac2 = (frac + '00').slice(0, 2);
  const pence = Number(whole) * 100 + Number(frac2);
  if (!Number.isFinite(pence)) return null;
  return neg ? -pence : pence;
}

function decimalStringFromNumber(n: number): string {
  if (!Number.isFinite(n)) return 'NaN';
  // Only for already-2dp values; callers should prefer numeric strings from pg.
  return (Math.round(n * 100) / 100).toFixed(2);
}

/** Display pounds from integer pence (boundary only). */
export function formatPenceGBP(pence: number): string {
  const neg = pence < 0;
  const abs = Math.abs(pence);
  const whole = Math.trunc(abs / 100);
  const frac = String(abs % 100).padStart(2, '0');
  const body = `£${whole.toLocaleString('en-GB')}.${frac}`;
  return neg ? `-${body}` : body;
}

export function vatBpsForTreatment(
  treatment: TaxTreatment,
  standardVatRateBps: number,
): number {
  if (treatment !== 'standard') return 0;
  if (!Number.isFinite(standardVatRateBps) || standardVatRateBps < 0) return 0;
  return Math.floor(standardVatRateBps);
}

export function vatRateLabelFromBps(bps: number): string {
  // 2000 → "20%", 1750 → "17.5%"
  const whole = Math.trunc(bps / 100);
  const frac = bps % 100;
  if (frac === 0) return `${whole}%`;
  const fracStr = String(frac).padStart(2, '0').replace(/0+$/, '');
  return `${whole}.${fracStr}%`;
}

/**
 * Per-line VAT in pence: round half away from zero of (net * bps / 10000).
 * Integer-only: (net * bps + sign*5000) / 10000 using truncation toward zero
 * after adding half-unit for away-from-zero rounding.
 */
export function lineVatPence(netPence: number, vatBps: number): number {
  if (netPence === 0 || vatBps === 0) return 0;
  const sign = netPence < 0 ? -1 : 1;
  const net = Math.abs(netPence);
  const product = net * vatBps;
  const rounded = Math.trunc((product + 5000) / 10000);
  return sign * rounded;
}

/** Legacy (incorrect for Xero): VAT on summed net — kept for comparison tests only. */
export function vatOnSummedNetPence(
  netLinesPence: number[],
  vatBps: number,
): number {
  const netTotal = netLinesPence.reduce((a, b) => a + b, 0);
  return lineVatPence(netTotal, vatBps);
}

export function computeVatTotals(netLinesPence: number[], vatBps: number) {
  const netTotal = netLinesPence.reduce((a, b) => a + b, 0);
  let vat = 0;
  for (const net of netLinesPence) {
    vat += lineVatPence(net, vatBps);
  }
  const gross = netTotal + vat;
  return {
    netTotal,
    vat,
    gross,
    rateBps: vatBps,
    rateLabel: vatRateLabelFromBps(vatBps),
  };
}

/** Calendar due date: invoice/run date + payment_terms_days. */
export function dueDateIso(runDateIso: string, paymentTermsDays: number): string {
  const days =
    Number.isFinite(paymentTermsDays) && paymentTermsDays > 0
      ? Math.floor(paymentTermsDays)
      : 30;
  const [y, m, d] = runDateIso.split('-').map(Number);
  const dt = new Date(Date.UTC(y!, m! - 1, d!));
  dt.setUTCDate(dt.getUTCDate() + days);
  const yy = dt.getUTCFullYear();
  const mm = String(dt.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(dt.getUTCDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

export function formatUkDate(isoDate: string): string {
  const [y, m, d] = isoDate.split('-');
  if (!y || !m || !d) return isoDate;
  return `${d}/${m}/${y}`;
}
